import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  fetchAssertion,
  fetchBadgeClass,
  fetchIssuer,
  isHttpError,
  type OpenBadgesAssertion,
  type OpenBadgesBadgeClass,
  type OpenBadgesIssuer,
} from '../lib/api';
import { Layout } from '../components/Layout';
import { Hex } from '../components/Hex';
import { VerifyCodeForm } from '../components/VerifyCodeForm';
// AddToLinkedIn + ShareMenu intentionally NOT imported here.
// Public verify page must remain read-only — those actions live in Talentia /me.

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'revoked'; assertion: OpenBadgesAssertion; badgeClass: OpenBadgesBadgeClass | null }
  | { kind: 'gone' }
  | { kind: 'error'; message: string }
  | {
      kind: 'valid';
      assertion: OpenBadgesAssertion;
      badgeClass: OpenBadgesBadgeClass | null;
      issuer: OpenBadgesIssuer | null;
    };

const CODE_PATTERN = /^[A-Z0-9-]{8,80}$/;

export function VerifyPage() {
  const { code } = useParams<{ code: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!code) { setState({ kind: 'not_found' }); return; }
    if (!CODE_PATTERN.test(code)) { setState({ kind: 'not_found' }); return; }

    let cancelled = false;
    (async () => {
      try {
        const a = await fetchAssertion(code);
        if (cancelled) return;
        if (isHttpError(a)) {
          if (a.httpStatus === 404) return setState({ kind: 'not_found' });
          if (a.httpStatus === 410) return setState({ kind: 'gone' });
          return setState({ kind: 'error', message: `HTTP ${a.httpStatus}` });
        }
        const [bc, iss] = await Promise.all([
          fetchBadgeClass(a.badge),
          fetchIssuer(extractIssuerUrl(a.badge)),
        ]);
        if (cancelled) return;
        const badgeClass = isHttpError(bc) ? null : bc;
        if (a.revoked) return setState({ kind: 'revoked', assertion: a, badgeClass });
        setState({
          kind: 'valid',
          assertion: a,
          badgeClass,
          issuer: isHttpError(iss) ? null : iss,
        });
      } catch (err) {
        if (cancelled) return;
        setState({ kind: 'error', message: err instanceof Error ? err.message : 'Unknown error' });
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  if (state.kind === 'loading') return <LoadingState />;
  if (state.kind === 'not_found') return <NotFoundState code={code} />;
  if (state.kind === 'gone') return <ConsentWithdrawnState code={code} />;
  if (state.kind === 'error') return <ErrorState message={state.message} />;
  if (state.kind === 'revoked') return <RevokedState assertion={state.assertion} badgeClass={state.badgeClass} code={code!} />;

  return <ValidCredential assertion={state.assertion} badgeClass={state.badgeClass} issuer={state.issuer} code={code!} />;
}

// =============================================================================
// Valid credential — editorial 2-column layout
// =============================================================================
function ValidCredential({
  assertion, badgeClass, issuer, code,
}: {
  assertion: OpenBadgesAssertion;
  badgeClass: OpenBadgesBadgeClass | null;
  issuer: OpenBadgesIssuer | null;
  code: string;
}) {
  const issuedDate = new Date(assertion.issuedOn);

  return (
    <Layout>
      <section>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-20">
          {/* Status indicator — checkmark badge with LED-style breathing glow.
              The pill enters with status-drop, then the inner shadow animation
              loops infinitely at ~3s cadence to communicate "operativo / live"
              without being distracting. */}
          <div
            className="inline-flex items-center gap-2.5 mb-10 text-[var(--success)] status-drop"
            style={{ ['--delay' as string]: '60ms' }}
          >
            <span
              className="relative inline-flex items-center justify-center w-6 h-6 rounded-full"
              style={{
                background: 'var(--success)',
                animation: 'verified-glow 3s ease-in-out infinite',
              }}
              aria-hidden="true"
            >
              {/* Checkmark — drawn in once on mount via stroke-dashoffset */}
              <svg
                viewBox="0 0 16 16"
                width="14"
                height="14"
                fill="none"
                stroke="#fff"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path
                  d="M3.5 8.5 L6.8 11.6 L12.5 5.2"
                  style={{
                    strokeDasharray: 18,
                    animation: 'check-draw 600ms ease-out 220ms both',
                  }}
                />
              </svg>
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em]">
              Credencial verificada
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,420px)_1fr] gap-12 lg:gap-20 items-start">
            {/* LEFT — badge anchor */}
            <div>
              <BadgeAnchor
                imageUrl={assertion.image || badgeClass?.image}
                name={badgeClass?.name ?? 'Credencial'}
              />
              <dl className="mt-10 space-y-5">
                {(assertion.recipientName ?? assertion.recipientRedacted) && (
                  <Field label="Otorgada a" delay={620}>
                    <div
                      className="text-[18px] sm:text-[20px] font-semibold text-[var(--bi-oscuro)] leading-tight tracking-tight mt-1"
                    >
                      {assertion.recipientName ?? assertion.recipientRedacted}
                    </div>
                  </Field>
                )}
                <Field label="Emitido por" delay={780}>
                  <div className="flex items-baseline gap-1 leading-none">
                    <span className="font-bold text-[15px] tracking-tight text-[var(--bi-oscuro)]">
                      BOOTCAMP
                    </span>
                    <span className="font-light text-[15px] tracking-tight text-[var(--bi-oscuro)]">
                      INSTITUTE
                    </span>
                  </div>
                  {issuer?.url && (
                    <a
                      href={issuer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[var(--bi-claro)] hover:text-[var(--bi-primario)] mt-1 inline-block"
                    >
                      {issuer.url.replace(/^https?:\/\//, '')} →
                    </a>
                  )}
                </Field>
                <Field label="Fecha de emisión" delay={860}>
                  <div className="font-semibold text-[15px] text-[var(--bi-oscuro)]">
                    {issuedDate.toLocaleDateString('es-CO', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </div>
                  <div className="text-xs text-[var(--bi-claro)] font-mono mt-0.5">
                    {issuedDate.toISOString()}
                  </div>
                </Field>
                {(assertion.expires || badgeClass?.validityYears) && (
                  <Field label="Vigencia" delay={900}>
                    {assertion.expires ? (
                      <>
                        <div className="font-semibold text-[15px] text-[var(--bi-oscuro)]">
                          Vence el {new Date(assertion.expires).toLocaleDateString('es-CO', {
                            year: 'numeric', month: 'long', day: 'numeric',
                          })}
                        </div>
                        <div className="text-xs text-[var(--bi-claro)] mt-0.5">
                          {badgeClass?.validityYears ?? 3} años desde emisión · alineado con AWS Cloud Practitioner
                        </div>
                      </>
                    ) : (
                      <div className="font-semibold text-[15px] text-[var(--bi-oscuro)]">
                        {badgeClass?.validityYears ?? 3} años desde emisión
                      </div>
                    )}
                  </Field>
                )}
                <Field label="Código verificable" delay={940}>
                  <code className="block font-mono text-[12px] sm:text-[13px] text-[var(--bi-primario)] bg-[var(--bi-ligero)] px-3 py-2 rounded select-all break-all">
                    {code}
                  </code>
                </Field>
                <Field label="Estándar" delay={1020}>
                  <a
                    href="https://www.imsglobal.org/Badges/OBv2p0/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-[15px] text-[var(--bi-oscuro)] hover:text-[var(--bi-primario)]"
                  >
                    Open Badges 2.0
                  </a>
                  <div className="text-xs text-[var(--bi-claro)] mt-0.5">IMS Global Learning Consortium</div>
                </Field>
              </dl>
            </div>

            {/* RIGHT — editorial content */}
            <div>
              <span className="accent-bar reveal-bar" style={{ ['--delay' as string]: '140ms' }} />
              <p className="eyebrow eyebrow--strong mt-5 reveal" style={{ ['--delay' as string]: '220ms' }}>{tagsToCategory(badgeClass?.tags ?? [])}</p>
              <h1 className="display-1 mt-3 reveal" style={{ fontSize: 'clamp(2.25rem, 4vw + 0.5rem, 3.5rem)', ['--delay' as string]: '300ms' }}>
                {splitTitle(badgeClass?.name ?? 'Credencial verificada')}
              </h1>

              {badgeClass?.description && (
                <p className="prose-measure mt-7 text-[16px] leading-relaxed text-[var(--bi-oscuro)] reveal" style={{ ['--delay' as string]: '440ms' }}>
                  {badgeClass.description}
                </p>
              )}

              {(badgeClass?.criteria?.layers?.length ?? 0) > 0 ? (
                <section className="mt-10 reveal" style={{ ['--delay' as string]: '560ms' }}>
                  <h2 className="eyebrow eyebrow--strong">Criterios de obtención</h2>
                  <p className="mt-2 text-[13px] text-[var(--bi-claro)] prose-measure">
                    El egresado obtiene la credencial cuando cumple todas las capas siguientes.
                  </p>
                  <ol className="mt-5 space-y-3">
                    {badgeClass!.criteria!.layers!.map((l) => (
                      <li
                        key={`${l.layer}-${l.name}`}
                        className="grid grid-cols-[auto_1fr] gap-x-4 sm:gap-x-6 items-baseline pb-3 border-b border-[var(--hairline)] last:border-b-0"
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="font-mono text-[11px] tabular-nums text-[var(--bi-claro)] font-semibold w-6 text-right">
                            {String(l.layer).padStart(2, '0')}
                          </span>
                          <span className="text-[13px] font-semibold text-[var(--bi-oscuro)] uppercase tracking-[0.04em]">
                            {l.name}
                          </span>
                        </div>
                        <div>
                          <div className="text-[14px] text-[var(--bi-oscuro)] leading-snug">
                            {l.criterion}
                          </div>
                          <div className="text-[12px] text-[var(--bi-claro)] mt-1 font-mono">
                            {l.threshold}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </section>
              ) : badgeClass?.criteria?.narrative ? (
                <section className="mt-10 reveal" style={{ ['--delay' as string]: '560ms' }}>
                  <h2 className="eyebrow eyebrow--strong">Criterios de obtención</h2>
                  <p className="mt-2 text-[14px] leading-relaxed text-[var(--bi-oscuro)] prose-measure">
                    {badgeClass.criteria.narrative}
                  </p>
                </section>
              ) : null}

              {badgeClass?.alignment && badgeClass.alignment.length > 0 && (
                <AlignmentSections alignment={badgeClass.alignment} baseDelay={860} />
              )}

              {/* Bootcamp Institute extension — assertion.skills[] populated by migration 0008.
                  Renders when badge_classes.skills array has values; safe for legacy badges (no render). */}
              {assertion.skills && assertion.skills.length > 0 && (
                <section className="mt-10 reveal" style={{ ['--delay' as string]: '1000ms' }}>
                  <h2 className="eyebrow eyebrow--strong">Competencias certificadas</h2>
                  <p className="mt-2 text-[13px] text-[var(--bi-claro)] prose-measure">
                    Habilidades que el egresado demuestra al obtener esta credencial.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {assertion.skills.map((skill, i) => (
                      <span
                        key={`skill-${i}`}
                        className="inline-flex items-center px-3 py-1.5 rounded-full border border-[var(--bi-claro)]/30 bg-[var(--bi-ligero)]/40 text-[var(--bi-primario)] text-[12px] font-semibold tracking-[0.02em]"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Primary actions: LinkedIn 1-click + share */}
              {/* Secondary links — public verify page is read-only.
                  Profile actions (Add to LinkedIn, Share) belong in the
                  authenticated /me view of Talentia to prevent credential
                  appropriation by anonymous visitors. */}
              <section
                className="mt-10 pt-6 border-t border-[var(--hairline)] flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-[12px] reveal"
                style={{ ['--delay' as string]: '1100ms' }}
              >
                <a
                  href={`/api/v1/verify/${code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-arrow inline-flex items-center gap-1.5 font-medium text-[var(--bi-primario)] hover:text-[var(--bi-energico)]"
                >
                  Ver JSON-LD raw
                  <span className="arrow" aria-hidden>→</span>
                </a>
                <Link
                  to="/about"
                  className="font-medium text-[var(--bi-claro)] hover:text-[var(--bi-primario)]"
                >
                  ¿Cómo funciona la verificación?
                </Link>
              </section>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

// =============================================================================
// Auxiliary state pages
// =============================================================================

function BadgeAnchor({ imageUrl, name }: { imageUrl?: string; name: string }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  return (
    <figure className="relative w-full max-w-[380px] mx-auto">
      <div
        className="relative aspect-square flex items-center justify-center badge-hover-frame"
        role="img"
        aria-label={`Insignia ${name}`}
        tabIndex={0}
      >
        {!loaded && !errored && imageUrl && (
          <div className="absolute inset-10 hex skeleton-shimmer" />
        )}
        {imageUrl && !errored ? (
          <img
            src={imageUrl}
            alt=""
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            className={`relative w-[78%] h-[78%] object-contain badge-hover-img ${loaded ? 'badge-anchor-reveal' : 'opacity-0'}`}
            style={{ ['--delay' as string]: '220ms' }}
          />
        ) : (
          <div
            className="relative w-[68%] h-[68%] hex flex items-center justify-center text-white text-6xl font-extrabold badge-anchor-reveal badge-hover-img"
            style={{
              background: 'var(--bi-oscuro)',
              ['--delay' as string]: '220ms',
            }}
          >
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
    </figure>
  );
}

function Field({ label, children, delay = 0 }: { label: string; children: React.ReactNode; delay?: number }) {
  return (
    <div className="reveal" style={{ ['--delay' as string]: `${delay}ms` }}>
      <dt className="eyebrow">{label}</dt>
      <dd className="mt-1.5">{children}</dd>
    </div>
  );
}

interface Alignment {
  targetName: string;
  targetUrl?: string;
  targetFramework?: string;
}

/**
 * Skills + cert path agrupados por framework.
 * - LinkedIn Skill Taxonomy → chips outline neutral (skills validadas)
 * - AWS Certification → chip dorado con ícono trophy (career path)
 * - AWS Training / otros → chip texto secundario (referencia programa)
 */
function AlignmentSections({ alignment, baseDelay = 860 }: { alignment: Alignment[]; baseDelay?: number }) {
  const linkedin = alignment.filter((a) => a.targetFramework === 'LinkedIn Skill Taxonomy');
  const certs = alignment.filter((a) => a.targetFramework === 'AWS Certification');
  const training = alignment.filter((a) => /Training/i.test(a.targetFramework ?? ''));
  const other = alignment.filter(
    (a) =>
      a.targetFramework !== 'LinkedIn Skill Taxonomy' &&
      a.targetFramework !== 'AWS Certification' &&
      !/Training/i.test(a.targetFramework ?? ''),
  );

  let delay = baseDelay;
  const stepDelay = () => {
    const d = delay;
    delay += 28;
    return d;
  };

  return (
    <section className="mt-8 reveal" style={{ ['--delay' as string]: `${baseDelay - 200}ms` }}>
      {linkedin.length > 0 && (
        <div className="mb-6">
          <h2 className="eyebrow eyebrow--strong">Habilidades validadas</h2>
          <p className="mt-1 text-[12px] text-[var(--bi-claro)] tracking-wide">
            {linkedin.length} aptitudes alineadas con LinkedIn Skill Taxonomy
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {linkedin.map((a, i) => (
              <SkillChip key={`l-${i}`} alignment={a} delay={stepDelay()} />
            ))}
          </div>
        </div>
      )}

      {certs.length > 0 && (
        <div className="mb-5">
          <h2 className="eyebrow eyebrow--strong">Ruta de certificación</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {certs.map((a, i) => (
              <CertChip key={`c-${i}`} alignment={a} delay={stepDelay()} />
            ))}
          </div>
        </div>
      )}

      {training.length > 0 && (
        <div className="mb-2">
          <h2 className="eyebrow eyebrow--strong">Programa de origen</h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {training.map((a, i) => (
              <TrainingChip key={`t-${i}`} alignment={a} delay={stepDelay()} />
            ))}
          </div>
        </div>
      )}

      {other.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {other.map((a, i) => (
            <SkillChip key={`o-${i}`} alignment={a} delay={stepDelay()} />
          ))}
        </div>
      )}
    </section>
  );
}

function SkillChip({ alignment, delay = 0 }: { alignment: Alignment; delay?: number }) {
  const inner = (
    <span className="skill-chip inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[var(--border)] hover:border-[var(--bi-primario)] hover:text-[var(--bi-primario)] rounded-full text-[12px] font-medium text-[var(--bi-oscuro)] transition-colors">
      <Hex size={9} fill="var(--bi-primario)" />
      {alignment.targetName}
    </span>
  );
  const wrappedClass = 'chip-stagger inline-block';
  const wrappedStyle = { ['--delay' as string]: `${delay}ms` };
  if (alignment.targetUrl) {
    return (
      <a
        href={alignment.targetUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={wrappedClass}
        style={wrappedStyle}
      >
        {inner}
      </a>
    );
  }
  return (
    <span className={wrappedClass} style={wrappedStyle}>
      {inner}
    </span>
  );
}

function CertChip({ alignment, delay = 0 }: { alignment: Alignment; delay?: number }) {
  const inner = (
    <span className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#FFF8EC] border border-[#E5B048] hover:border-[#C8941E] rounded-md text-[12.5px] font-semibold text-[#7A5800] transition-colors">
      <TrophyIcon />
      {alignment.targetName}
    </span>
  );
  const wrappedClass = 'chip-stagger inline-block';
  const wrappedStyle = { ['--delay' as string]: `${delay}ms` };
  if (alignment.targetUrl) {
    return (
      <a
        href={alignment.targetUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={wrappedClass}
        style={wrappedStyle}
        aria-label={`Más información: ${alignment.targetName}`}
      >
        {inner}
      </a>
    );
  }
  return (
    <span className={wrappedClass} style={wrappedStyle}>
      {inner}
    </span>
  );
}

function TrainingChip({ alignment, delay = 0 }: { alignment: Alignment; delay?: number }) {
  const inner = (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[var(--bi-claro)] tracking-wide">
      <span className="opacity-60">›</span>
      {alignment.targetName}
    </span>
  );
  const wrappedClass = 'chip-stagger inline-block';
  const wrappedStyle = { ['--delay' as string]: `${delay}ms` };
  if (alignment.targetUrl) {
    return (
      <a
        href={alignment.targetUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${wrappedClass} hover:text-[var(--bi-primario)] transition-colors`}
        style={wrappedStyle}
      >
        {inner}
      </a>
    );
  }
  return (
    <span className={wrappedClass} style={wrappedStyle}>
      {inner}
    </span>
  );
}

function TrophyIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );
}

function LoadingState() {
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-20">
        <div className="h-6 w-40 bg-[var(--bi-ligero)] rounded-full animate-pulse mb-8" />
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,420px)_1fr] gap-12 lg:gap-20">
          <div className="aspect-square hex bg-[var(--bi-ligero)]/40 animate-pulse max-w-[420px]" />
          <div className="space-y-4">
            <div className="h-1 w-14 bg-[var(--bi-primario)]" />
            <div className="h-12 w-3/4 bg-[var(--bi-ligero)] rounded animate-pulse" />
            <div className="h-12 w-1/2 bg-[var(--bi-ligero)] rounded animate-pulse" />
            <div className="space-y-2 mt-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-3 bg-[var(--bi-ligero)] rounded animate-pulse" style={{ width: `${85 - i * 7}%` }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function NotFoundState({ code }: { code: string | undefined }) {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-20 pb-32">
        {/* Hero */}
        <span className="accent-bar accent-bar--energico" />
        <p className="eyebrow eyebrow--strong mt-5">Verificación · Resultado</p>
        <h1 className="heading-1 mt-3">Credencial no encontrada</h1>
        <p className="text-[16px] mt-6 text-[var(--bi-oscuro)] leading-relaxed prose-measure">
          No encontramos ninguna credencial emitida por Bootcamp Institute que coincida
          con el código consultado. El verificador está operativo — revisa los
          detalles a continuación.
        </p>

        {/* Failed code — copy-friendly mono pill */}
        {code && (
          <div className="mt-8">
            <p className="eyebrow">Código consultado</p>
            <code
              className="block mt-2 font-mono text-[13px] sm:text-[14px] text-[var(--bi-primario)] bg-[var(--bi-ligero)] px-3 py-2.5 rounded select-all break-all"
            >
              {code}
            </code>
          </div>
        )}

        {/* Inline retry form */}
        <section className="mt-12 pt-8 border-t border-[var(--hairline)]">
          <p className="eyebrow eyebrow--strong">Verifica otro código</p>
          <p className="mt-2 text-[14px] text-[var(--bi-claro)] prose-measure">
            Si crees que tipeaste el código incorrectamente, vuelve a intentarlo aquí.
          </p>
          <div className="mt-5">
            <VerifyCodeForm
              autoFocus
              label="Código verificable"
              submitLabel="Verificar"
              placeholder="BG-BOOTCAMP-COBOG20-XXXXXX-XXXX"
            />
          </div>
        </section>

        {/* Causas frecuentes */}
        <section className="mt-12 pt-8 border-t border-[var(--hairline)]">
          <p className="eyebrow eyebrow--strong">Causas frecuentes</p>
          <ul className="mt-5 space-y-3">
            <CauseItem
              title="Error de tipeo"
              detail={
                <>
                  Verifica los caracteres ambiguos: <code className="font-mono bg-[var(--bi-ligero)] text-[var(--bi-primario)] px-1 py-0.5 rounded">0</code> (cero) vs{' '}
                  <code className="font-mono bg-[var(--bi-ligero)] text-[var(--bi-primario)] px-1 py-0.5 rounded">O</code> (letra O),{' '}
                  <code className="font-mono bg-[var(--bi-ligero)] text-[var(--bi-primario)] px-1 py-0.5 rounded">8</code> vs{' '}
                  <code className="font-mono bg-[var(--bi-ligero)] text-[var(--bi-primario)] px-1 py-0.5 rounded">B</code>,{' '}
                  <code className="font-mono bg-[var(--bi-ligero)] text-[var(--bi-primario)] px-1 py-0.5 rounded">1</code> vs{' '}
                  <code className="font-mono bg-[var(--bi-ligero)] text-[var(--bi-primario)] px-1 py-0.5 rounded">I</code>.
                </>
              }
            />
            <CauseItem
              title="Enlace truncado"
              detail="Algunos clientes de correo cortan URLs largas. Pide el código original al portador o copia la URL completa desde la fuente."
            />
            <CauseItem
              title="Credencial aún no emitida"
              detail="La cohorte puede estar en proceso de graduación. La emisión de credenciales ocurre tras el cierre formal del programa."
            />
            <CauseItem
              title="Credencial revocada"
              detail="Si la credencial fue revocada, el verificador lo indicará explícitamente — no aparece como “no encontrada”."
            />
          </ul>
        </section>

        {/* Reassurance + contact footer */}
        <section className="mt-12 pt-8 border-t border-[var(--hairline)] flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-[13px]">
          <Link
            to="/about"
            className="link-arrow inline-flex items-center gap-1.5 font-medium text-[var(--bi-primario)] hover:text-[var(--bi-energico)]"
          >
            ¿Cómo funciona la verificación?
            <span className="arrow" aria-hidden>→</span>
          </Link>
          <a
            href="mailto:verify@bootcamp-institutex.com"
            className="font-medium text-[var(--bi-claro)] hover:text-[var(--bi-primario)]"
          >
            Contactar a verify@bootcamp-institutex.com
          </a>
        </section>
      </div>
    </Layout>
  );
}

function CauseItem({ title, detail }: { title: string; detail: React.ReactNode }) {
  return (
    <li className="grid grid-cols-[auto_1fr] gap-x-4 sm:gap-x-6 items-baseline">
      <Hex size={9} fill="var(--bi-primario)" />
      <div>
        <div className="text-[14px] font-semibold text-[var(--bi-oscuro)] leading-snug">
          {title}
        </div>
        <div className="text-[13px] text-[var(--bi-claro)] mt-1 leading-relaxed prose-measure">
          {detail}
        </div>
      </div>
    </li>
  );
}

function ConsentWithdrawnState({ code }: { code: string | undefined }) {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-20 pb-32">
        <span className="accent-bar" />
        <p className="eyebrow eyebrow--strong mt-5">Habeas Data · LFPDPPP / Ley 1581</p>
        <h1 className="heading-1 mt-3">No disponible públicamente</h1>
        <p className="text-[16px] mt-6 text-[var(--bi-oscuro)] leading-relaxed max-w-prose">
          El portador de esta credencial retiró su consentimiento de verificación pública,
          en cumplimiento de las regulaciones de protección de datos personales.
        </p>
        <p className="text-sm text-[var(--bi-claro)] mt-4">
          Para validar esta credencial, contacta directamente al portador o a{' '}
          <a className="text-[var(--bi-primario)] hover:underline font-medium" href="mailto:soporte@bootcamp-institutex.com">
            soporte@bootcamp-institutex.com
          </a>
          .
        </p>
        {code && (
          <p className="text-xs text-[var(--bi-claro)] mt-6 font-mono">
            Código: <code className="bg-[var(--bi-ligero)] text-[var(--bi-primario)] px-1.5 py-0.5 rounded">{code}</code>
          </p>
        )}
      </div>
    </Layout>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-20 pb-32">
        <span className="accent-bar accent-bar--energico" />
        <h1 className="heading-1 mt-5">Error al verificar</h1>
        <p className="text-[16px] mt-6 text-[var(--bi-oscuro)] leading-relaxed max-w-prose">
          No pudimos verificar la credencial en este momento. Por favor intenta nuevamente.
        </p>
        <p className="text-xs text-[var(--bi-claro)] mt-4 font-mono">Detalle técnico: {message}</p>
      </div>
    </Layout>
  );
}

function RevokedState({
  assertion, badgeClass, code,
}: {
  assertion: OpenBadgesAssertion;
  badgeClass: OpenBadgesBadgeClass | null;
  code: string;
}) {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 pb-20">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-8 bg-[var(--error-ligero)] text-[var(--error)]">
          <Hex size={9} fill="var(--error)" />
          <span className="text-[11px] font-bold uppercase tracking-[0.12em]">
            Credencial revocada
          </span>
        </div>
        <span className="accent-bar" style={{ background: 'var(--error)' }} />
        <h1 className="heading-1 mt-5" style={{ color: 'var(--error)' }}>
          {badgeClass?.name ?? 'Credencial'}
        </h1>
        <p className="text-[16px] mt-6 text-[var(--bi-oscuro)] leading-relaxed max-w-prose">
          Esta credencial fue revocada por el emisor y ya no es válida.
          Permanece registrada en el sistema bajo retención FOSFEC de 5 años para fines de auditoría.
        </p>
        {assertion.revocationReason && (
          <div className="mt-6 p-4 bg-[var(--error-ligero)] border-l-4 border-[var(--error)] rounded">
            <p className="eyebrow" style={{ color: 'var(--error)' }}>Razón</p>
            <p className="mt-2 text-sm text-[var(--bi-oscuro)] leading-relaxed">{assertion.revocationReason}</p>
          </div>
        )}
        <p className="text-xs text-[var(--bi-claro)] mt-10 font-mono">
          Código: <code className="bg-[var(--bi-ligero)] text-[var(--bi-primario)] px-1.5 py-0.5 rounded">{code}</code>
        </p>
      </div>
    </Layout>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function extractIssuerUrl(badgeUrl: string): string {
  try {
    const u = new URL(badgeUrl);
    return `${u.origin}/issuer`;
  } catch {
    return '/issuer';
  }
}

/**
 * Split a badge title into Bold-first-word + Light-rest, matching the
 * brand's two-tone heading pattern from the manual:
 *   "FUNDAMENTOS DE CLOUD COMPUTING" → <strong>FUNDAMENTOS</strong> DE CLOUD...
 */
function splitTitle(title: string): React.ReactNode {
  const idx = title.indexOf(' ');
  if (idx === -1) return <strong>{title}</strong>;
  return (
    <>
      <strong style={{ fontWeight: 800 }}>{title.slice(0, idx)}</strong>
      <span style={{ fontWeight: 300 }}>{title.slice(idx)}</span>
    </>
  );
}

function tagsToCategory(tags: string[]): string {
  if (tags.includes('arquitectura')) return 'Arquitectura · Apply + Analyze + Evaluate';
  if (tags.includes('desarrollo')) return 'Desarrollo · Apply + Analyze';
  if (tags.includes('fundamentos')) return 'Fundamentos · Apply';
  if (tags.includes('graduation')) return 'Graduación';
  if (tags.includes('milestone')) return 'Hito de cohort';
  if (tags.includes('skill')) return 'Habilidad demostrada';
  return 'Credencial verificable';
}
